import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Toolbar,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import { gql, useLazyQuery } from "@apollo/client";

// -------------------- Styles --------------------
const useStyles = makeStyles((theme) => ({
  card: { padding: theme.spacing(2) },
  sectionHeader: { marginBottom: theme.spacing(1) },
  actionsRow: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: 0,
    paddingRight: 0,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 560, // sticky header viewport
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));

// -------------------- GraphQL --------------------
const FILE_REPORT = gql`
  query FileReport($email: String, $fromDate: Date, $toDate: Date, $matchScope: String) {
    fileReport(email: $email, fromDate: $fromDate, toDate: $toDate, matchScope: $matchScope) {
      fileId
      companyName
      filename
      uploaderEmail
      downloaderEmail
      uploadDate
      downloadDate
    }
  }
`;

// -------------------- Utils --------------------
const toIsoDateOrNull = (s) => {
  if (!s) return null;
  const v = String(s).trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already ISO date
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [mm, dd, yyyy] = v.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

const fmtLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const toCsv = (rows) => {
  const headers = ["FileId", "CompanyName", "FileName", "UploadDate", "DownloadDate"];
  const esc = (val) => {
    if (val == null) return "";
    const s = String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.fileId,
        r.companyName,
        r.filename,
        r.uploadDate ? new Date(r.uploadDate).toISOString() : "",
        r.downloadDate ? new Date(r.downloadDate).toISOString() : "",
      ]
        .map(esc)
        .join(",")
    ),
  ];
  return lines.join("\n");
};

// Sorting helpers
function descendingComparator(a, b, orderBy) {
  const av = a[orderBy];
  const bv = b[orderBy];
  if (orderBy === "uploadDate" || orderBy === "downloadDate") {
    const ad = av ? new Date(av).getTime() : 0;
    const bd = bv ? new Date(bv).getTime() : 0;
    if (bd < ad) return -1;
    if (bd > ad) return 1;
    return 0;
  }
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

// -------------------- Component --------------------
export const FileUploadReport = () => {
  const classes = useStyles();

  // Inputs
  const [userEmail, setUserEmail] = useState("");
   const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [matchScope, setMatchScope] = useState("downloader"); // 'downloader' | 'uploader'
  const [quickFilter, setQuickFilter] = useState("");

  // Table state
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("downloadDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Data
  const [runReport, { data, loading, error }] = useLazyQuery(FILE_REPORT, {
    fetchPolicy: "network-only",
    onError: (e) => console.error("GraphQL error:", e?.message || e),
  });
  const rawRows = data?.fileReport ?? [];

  // Quick filter
  const quickFiltered = useMemo(() => {
    const q = quickFilter.trim().toLowerCase();
    if (!q) return rawRows;
    return rawRows.filter((r) => {
      return (
        String(r.fileId ?? "").toLowerCase().includes(q) ||
        String(r.companyName ?? "").toLowerCase().includes(q) ||
        String(r.filename ?? "").toLowerCase().includes(q) ||
        String(r.uploaderEmail ?? "").toLowerCase().includes(q) ||
        String(r.downloaderEmail ?? "").toLowerCase().includes(q) ||
        fmtLocal(r.uploadDate).toLowerCase().includes(q) ||
        fmtLocal(r.downloadDate).toLowerCase().includes(q)
      );
    });
  }, [rawRows, quickFilter]);

  // sorting & pagination
  const sorted = useMemo(
    () => stableSort(quickFiltered, getComparator(order, orderBy)),
    [quickFiltered, order, orderBy]
  );
  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  // Table handlers
  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Actions
  const handleSearch = () => {
    if (!userEmail.trim()) {
      alert("Please enter a User Email.");
      return;
    }
    setPage(0);
    runReport({
      variables: {
        email: userEmail.trim(),
        fromDate: toIsoDateOrNull(fromDate),
        toDate: toIsoDateOrNull(toDate),
        matchScope, // 'downloader' | 'uploader'
      },
    });
  };

  const handleExport = () => {
    if (!sorted.length) {
      alert("No data to export.");
      return;
    }
    const csv = toCsv(sorted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `file-report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Table columns
  const headCells = [
    { id: "companyName", label: "Company" },
    { id: "filename", label: "File Name" },
    { id: "uploadDate", label: "Uploaded" },
    { id: "downloadDate", label: "Downloaded" },
    { id: "uploaderEmail", label: "Uploaded Email" },
    { id: "downloadEmail", label: "Downloaded Email" },
  ];

  return (
    <Container maxWidth="xl" disableGutters>
      <Grid container>
        <Typography variant="h4">File Report</Typography>
      </Grid>

      <Paper className={classes.card} style={{ marginTop: 10 }}>
        <Typography variant="subtitle1" className={classes.sectionHeader}>
          User Email and Date Range
        </Typography>

        <Grid container spacing={2}>
          {/* Email */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="User Email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="From Date"
              type="date"
              value={toIsoDateOrNull(fromDate) || ""}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="To Date"
              type="date"
              value={toIsoDateOrNull(toDate) || ""}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Match Scope - only two options */}
          <Grid item xs={12}>
            <FormLabel component="legend">Filter by</FormLabel>
            <RadioGroup
              row
              value={matchScope}
              onChange={(e) => setMatchScope(e.target.value)}
            >
              <FormControlLabel
                value="downloader"
                label="Downloader"
                control={<Radio color="primary" />}
              />
              <FormControlLabel
                value="uploader"
                label="Uploader"
                control={<Radio color="primary" />}
              />
            </RadioGroup>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: 16, marginBottom: 12 }} />

        <div className={classes.actionsRow}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "SEARCH REPORT"}
          </Button>

          <TextField
            variant="outlined"
            size="small"
            label="Quick filter"
            placeholder="Type to filter results"
            value={quickFilter}
            onChange={(e) => setQuickFilter(e.target.value)}
          />

          <Button
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={!sorted.length}
          >
            EXPORT CSV
          </Button>
        </div>

        {/* TABLE */}
        <Toolbar className={classes.toolbar}>
          <Typography variant="subtitle2" color="textSecondary">
            {sorted.length.toLocaleString()} result(s)
          </Typography>
        </Toolbar>

        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small" aria-label="file report table">
            <TableHead>
              <TableRow>
                {headCells.map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={classes.stickyHeader}
                    sortDirection={orderBy === cell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === cell.id}
                      direction={orderBy === cell.id ? order : "asc"}
                      onClick={(e) => handleRequestSort(e, cell.id)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((r, idx) => (
                <TableRow
                  key={`${r.fileId}-${r.downloadDate || r.uploadDate || idx}`}
                  hover
                >
                  <TableCell>{r.companyName}</TableCell>
                  <TableCell>{r.filename}</TableCell>
                  <TableCell>{fmtLocal(r.uploadDate)}</TableCell>
                  <TableCell>{fmtLocal(r.downloadDate)}</TableCell>
                  <TableCell>{r.uploaderEmail}</TableCell>
                  <TableCell>{r.downloaderEmail}</TableCell>
                </TableRow>
              ))}

              {/* Empty state row */}
              {!loading && paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="textSecondary">
                      No records yet. Enter an email, select dates, choose
                      "Downloader" or "Uploader", and click Search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* IMPORTANT: MUI v4 prop names */}
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onChangePage={handleChangePage}              /* v4 */
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}/* v4 */
          rowsPerPageOptions={[25, 50, 100]}
        />

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error.message}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default FileUploadReport;
